import React, {useContext, useEffect, useRef, useState} from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Dashboard.css';
import {WidgetTable} from './Widgets/WidgetTable';
import {WidgetActivity} from './Widgets/WidgetActivity';
import {WidgetWelcome} from './Widgets/WidgetWelcome';
import {WidgetFavorites} from './Widgets/WidgetFavorites';
import {WidgetRecent} from './Widgets/WidgetRecent';
import {WidgetTicketsSupport, WidgetTicketsCommercial} from './Widgets/WidgetTickets';
import {getBreakpoint, useWindowSize} from '../../hooks/useWindowSize';
import WidgetSidebar from './WidgetSidebar/WidgetSidebar';
import {Fab, Tooltip} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WidgetsIcon from '@mui/icons-material/Widgets';
import CloseIcon from '@mui/icons-material/Close';
import {TourProvider, useTour} from '@reactour/tour';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {LayoutDashbordContext} from "../../Context/LayoutDashbordContext";
import {useTranslation} from "../../hooks/useTranslation";



// Définir les tailles minimales par widget
const widgetMinSizes = {
    'table-orders': { minW: 5, minH: 4, defaultW: 6, defaultH: 5 },
    'activity-recent': { minW: 3, minH: 5, defaultW: 6, defaultH: 5 },
    'welcome-clock': { minW: 3, minH: 4, defaultW: 5, defaultH: 5 },
    'apps-favorites': { minW: 2, minH: 5, defaultW: 6, defaultH: 6 },
    'apps-recent': { minW: 3, minH: 3, defaultW: 4, defaultH: 4 },
    'tickets-support': { minW: 3, minH: 4, defaultW: 6, defaultH: 5 },
    'tickets-commercial': { minW: 3, minH: 4, defaultW: 6, defaultH: 5 },
};


const DashboardContent = () => {
    const t = useTranslation('Dashboard.json');
    const windowSize = useWindowSize();
    const breakpoint = getBreakpoint(windowSize.width);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(1200);
    const [isEditMode, setIsEditMode] = useState(false);
    const [widgetSidebarOpen, setWidgetSidebarOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const { setIsOpen: setTourOpen, setCurrentStep, currentStep, isOpen: tourIsOpen } = useTour();
    const layoutContext = useContext(LayoutDashbordContext)



    const [layout, setLayout] = useState(layoutContext.layout[breakpoint] || []);
    const gridRef = useRef(null);

    // Configuration par breakpoint (défini avant pour être utilisé par adaptWidgetToBreakpoint)
    const gridConfig = {
        desktop: { cols: 12, rowHeight: 60 },
        tablet: { cols: 6, rowHeight: 60 },
        mobile: { cols: 6, rowHeight: 50 },
    };

    // Mettre à jour le layout quand le breakpoint change
    useEffect(() => {
        setLayout(layoutContext.layout[breakpoint] || []);
    }, [breakpoint]);

    // Calculer la largeur du container en tenant compte du padding
    useEffect(() => {
        if (!containerRef.current) return;

        const updateWidth = () => {
            const width = containerRef.current.offsetWidth;
            // Soustraire le padding selon le breakpoint
            let padding = 40; // Desktop: 20px * 2
            if (breakpoint === 'tablet') padding = 32; // 16px * 2
            if (breakpoint === 'mobile') padding = 24; // 12px * 2

            setContainerWidth(width - padding);
        };

        // Mise à jour initiale
        updateWidth();

        // Observer les changements de taille du container
        const resizeObserver = new ResizeObserver(() => {
            updateWidth();
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [windowSize, breakpoint]);

    // Synchroniser les widgets (ID seulement) entre breakpoints
    const syncWidgetsBetweenBreakpoints = (widgetIds, action = 'add') => {
        ['desktop', 'tablet', 'mobile'].forEach(bp => {
            const savedLayout = localStorage.getItem(`dashboardLayout_${bp}`);
            let currentLayout = savedLayout ? JSON.parse(savedLayout) : [];

            if (action === 'add') {
                // Ajouter les nouveaux widgets
                widgetIds.forEach(widgetId => {
                    if (!currentLayout.some(item => item.i === widgetId)) {
                        const sizes = widgetMinSizes[widgetId] || { minW: 4, minH: 4, defaultW: 4, defaultH: 4 };

                        // Sur mobile : pleine largeur et empilé
                        if (bp === 'mobile') {
                            currentLayout.push({
                                i: widgetId,
                                x: 0,
                                y: currentLayout.length * 5, // Empiler verticalement
                                w: 6, // Pleine largeur
                                h: sizes.defaultH,
                                minW: 4,
                                minH: sizes.minH,
                            });
                        } else {
                            // Desktop/Tablet : garder les dimensions par défaut
                            currentLayout.push({
                                i: widgetId,
                                x: 0,
                                y: Infinity, // react-grid-layout trouvera la position
                                w: sizes.defaultW,
                                h: sizes.defaultH,
                                minW: sizes.minW,
                                minH: sizes.minH,
                            });
                        }
                    }
                });
            } else if (action === 'remove') {
                // Supprimer les widgets
                currentLayout = currentLayout.filter(item => !widgetIds.includes(item.i));
            }

            localStorage.setItem(`dashboardLayout_${bp}`, JSON.stringify(currentLayout));
        });
    };

    const onLayoutChange = (newLayout) => {
        setLayout(newLayout);
        // Sauvegarder uniquement le layout actuel, pas de synchronisation
        localStorage.setItem(`dashboardLayout_${breakpoint}`, JSON.stringify(newLayout));
    };

    // Récupérer le layout sauvegardé pour le breakpoint actuel
    useEffect(() => {
        const savedLayout = localStorage.getItem(`dashboardLayout_${breakpoint}`);
        if (savedLayout) {
            let parsedLayout = JSON.parse(savedLayout);

            // Sur mobile et tablette : forcer tous les widgets à x=0, pleine largeur et empilés
            if (breakpoint === 'mobile' || breakpoint === 'tablet') {
                const cols = gridConfig[breakpoint].cols;
                parsedLayout = parsedLayout
                    .sort((a, b) => a.y - b.y || a.x - b.x) // Trier par position
                    .map((item, index) => ({
                        ...item,
                        x: 0,
                        w: cols, // Pleine largeur selon le breakpoint
                        y: index * (item.h || 5), // Empiler verticalement
                    }));
            }

            setLayout(parsedLayout);
        } else {
            setLayout(layoutContext.layout[breakpoint] || []);
        }
    }, [breakpoint]);

    const toggleEditMode = () => {
        const newEditMode = !isEditMode;
        setIsEditMode(newEditMode);
        if (newEditMode) {
            // Si le tour est en cours et à l'étape du bouton lock, passer à l'étape suivante
            if (tourIsOpen && currentStep === 0) {
                setTimeout(() => setCurrentStep(1), 300);
            }
        } else {
            setWidgetSidebarOpen(false);
            // Si le tour est en cours et à l'étape de verrouillage, passer à l'étape finale

            let layoutToSave = {};
            ['desktop', 'tablet', 'mobile'].forEach((bp )=> {
                const savedLayout = localStorage.getItem(`dashboardLayout_${bp}`);
                layoutToSave[bp] = savedLayout ? JSON.parse(savedLayout) : []

            })
            layoutContext.setLayout(layoutToSave)


            if (tourIsOpen && currentStep === 6) {
                setTimeout(() => setCurrentStep(7), 300);
            }
        }
    };

    const handleWidgetSidebarToggle = () => {
        const newState = !widgetSidebarOpen;
        setWidgetSidebarOpen(newState);

        // Si le tour est en cours et à l'étape du bouton widgets, passer à l'étape suivante
        if (tourIsOpen && currentStep === 1 && newState) {
            setTimeout(() => setCurrentStep(2), 600);
        }
    };

    // Ouvrir automatiquement la sidebar quand on arrive à l'étape 2
    useEffect(() => {
        if (tourIsOpen && currentStep === 2 && !widgetSidebarOpen) {
            setWidgetSidebarOpen(true);
        }
    }, [tourIsOpen, currentStep]);

    const startTour = () => {
        // Réinitialiser l'état
        setIsEditMode(false);
        setWidgetSidebarOpen(false);
        setTourOpen(true);
        setCurrentStep(0);
    };

    // Gérer l'ouverture/fermeture de la sidebar selon l'étape du tour
    useEffect(() => {
        if (tourIsOpen) {
            // Étape 3: ouvrir la sidebar pour montrer les widgets
            if (currentStep === 3) {
                setWidgetSidebarOpen(true);
            }
            // Étapes 4, 5, 6: fermer la sidebar pour voir les widgets
            else if (currentStep >= 4 && currentStep <= 6) {
                setWidgetSidebarOpen(false);
            }
        }
    }, [currentStep, tourIsOpen]);

    const handleRemoveWidget = (widgetId) => {
        const newLayout = layout.filter(item => item.i !== widgetId);
        setLayout(newLayout);

        // Supprimer le widget sur tous les breakpoints
        let layoutToSave = {};
        ['desktop', 'tablet', 'mobile'].forEach(bp => {
            const savedLayout = localStorage.getItem(`dashboardLayout_${bp}`);
            if (savedLayout) {
                const parsedLayout = JSON.parse(savedLayout);
                const filteredLayout = parsedLayout.filter(item => item.i !== widgetId);
                localStorage.setItem(`dashboardLayout_${bp}`, JSON.stringify(filteredLayout));
                layoutToSave[bp] = filteredLayout;
            } else {
                layoutToSave[bp] = [];
            }
        });

        // Mettre à jour le contexte immédiatement
        layoutContext.setLayout(layoutToSave);
    };

    const handleAddWidget = (widgetType, widgetId) => {
        // Logique pour ajouter un widget au dashboard
        console.log('Add widget:', widgetType, widgetId);
    };

    const handleDrop = (layoutParam, layoutItem, event) => {
        event.preventDefault();
        const widgetType = event.dataTransfer.getData('widgetType');
        const widgetId = event.dataTransfer.getData('widgetId');

        if (!widgetId) {
            return;
        }

        // Vérifier si le widget n'est pas déjà présent
        const widgetExists = layout.some(item => item.i === widgetId);
        if (widgetExists) {
            return;
        }

        // Récupérer les tailles pour ce widget
        const sizes = widgetMinSizes[widgetId] || { minW: 4, minH: 4, defaultW: 4, defaultH: 4 };

        // Créer le nouveau widget avec les propriétés du layoutItem
        const newWidget = {
            i: widgetId,
            x: layoutItem.x,
            y: layoutItem.y,
            w: sizes.defaultW,
            h: sizes.defaultH,
            minW: sizes.minW,
            minH: sizes.minH,
        };

        // Filtrer les widgets temporaires et ajouter le nouveau
        const newLayout = layout
            .filter(item => item.i !== '__dropping-elem__')
            .concat(newWidget);

        setLayout(newLayout);

        // Sauvegarder le layout actuel
        localStorage.setItem(`dashboardLayout_${breakpoint}`, JSON.stringify(newLayout));

        // Synchroniser le nouveau widget sur tous les autres breakpoints
        syncWidgetsBetweenBreakpoints([widgetId], 'add');

        // Rouvrir la sidebar après le drop
        setTimeout(() => {
            setWidgetSidebarOpen(true);
        }, 300);
    };

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const handleCancelDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        // Rouvrir la sidebar
        setTimeout(() => {
            setWidgetSidebarOpen(true);
        }, 100);
    };

    return (
        <>
            {isDragging && (
                <div
                    className="cancel-drop-zone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleCancelDrop}
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '200px',
                        height: '100vh',
                        background: 'linear-gradient(90deg, transparent, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.3))',
                        borderLeft: '3px dashed #f44336',
                        zIndex: 1400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(244, 67, 54, 0.3)',
                        textAlign: 'center',
                        fontFamily: 'Poppins'
                    }}>
                        <CloseIcon sx={{ fontSize: 48, color: '#f44336', mb: 1 }} />
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginTop: '8px' }}>
                            {t('dashboard.cancel_drop.title')}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                            {t('dashboard.cancel_drop.subtitle')}
                        </div>
                    </div>
                </div>
            )}

            <div className="dashboard-container" ref={containerRef}>
                <div className="dashboard-header">
                    <h1>{t('dashboard.title')}</h1>
                    <div className="dashboard-controls">
                        <Tooltip title={t('dashboard.tooltip.guide')}>
                            <Fab
                                size="small"
                                onClick={startTour}
                                sx={{
                                    backgroundColor: 'background.paper',
                                    color: '#FF7233',
                                    mr: 1
                                }}
                            >
                                <HelpOutlineIcon />
                            </Fab>
                        </Tooltip>

                        <Tooltip title={isEditMode ? t('dashboard.tooltip.lock') : t('dashboard.tooltip.unlock')}>
                            <Fab
                                data-tour="lock-button"
                                size="medium"
                                onClick={toggleEditMode}
                                sx={{
                                    backgroundColor: isEditMode ? '#FF7233' : 'background.paper',
                                    color: isEditMode ? 'white' : '#FF7233',
                                    '&:hover': {
                                        backgroundColor: isEditMode ? '#e66429' : 'action.hover',
                                    },
                                    mr: 1
                                }}
                            >
                                {isEditMode ? <LockOpenIcon /> : <LockIcon />}
                            </Fab>
                        </Tooltip>

                        {isEditMode && (
                            <Tooltip title={t('dashboard.tooltip.widgets')}>
                                <Fab
                                    data-tour="widgets-button"
                                    size="medium"
                                    onClick={handleWidgetSidebarToggle}
                                    sx={{
                                        backgroundColor: widgetSidebarOpen ? '#FF7233' : 'background.paper',
                                        color: widgetSidebarOpen ? 'white' : '#FF7233',
                                        '&:hover': {
                                            backgroundColor: widgetSidebarOpen ? '#e66429' : 'action.hover',
                                        }
                                    }}
                                >
                                    <WidgetsIcon />
                                </Fab>
                            </Tooltip>
                        )}
                    </div>
                </div>

                <GridLayout
                    ref={gridRef}
                    className="layout"
                    layout={layout}
                    cols={gridConfig[breakpoint].cols}
                    rowHeight={gridConfig[breakpoint].rowHeight}
                    width={containerWidth}
                    onLayoutChange={onLayoutChange}
                    onDrop={handleDrop}
                    isDroppable={isEditMode}
                    isDraggable={isEditMode && breakpoint !== 'mobile'}
                    isResizable={isEditMode && breakpoint !== 'mobile'}
                    compactType="vertical"
                    preventCollision={false}
                    draggableHandle=".widget-header"
                    droppingItem={{ i: '__dropping-elem__', w: 4, h: 4 }}
                >
                    {layout.filter(item => item.i !== '__dropping-elem__').map((item) => {
                        const widgetMap = {
                            'table-orders': <WidgetTable
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'activity-recent': <WidgetActivity
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'welcome-clock': <WidgetWelcome
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'apps-favorites': <WidgetFavorites
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'apps-recent': <WidgetRecent
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'tickets-support': <WidgetTicketsSupport
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                            'tickets-commercial': <WidgetTicketsCommercial
                                onRemove={() => handleRemoveWidget(item.i)}
                                isEditMode={isEditMode}
                            />,
                        };

                        return (
                            <div key={item.i} className="widget">
                                {widgetMap[item.i] || (
                                    <div className="widget-content">
                                        <div className="widget-header">
                                            <h3>{t('dashboard.widget_not_found.title')}</h3>
                                            {isEditMode && (
                                                <div
                                                    className="widget-remove-btn"
                                                    onClick={() => handleRemoveWidget(item.i)}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            )}
                                        </div>
                                        <div className="widget-body">
                                            <p>{t('dashboard.widget_not_found.body', [item.i])}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </GridLayout>
            </div>

            <WidgetSidebar
                isOpen={widgetSidebarOpen}
                setIsOpen={setWidgetSidebarOpen}
                currentWidgets={layout}
                onAddWidget={handleAddWidget}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            />
        </>
    );
};

const tourButtonStyle = {
    background: '#FF7233',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '14px',
    width: '100%'
};

function buildTourSteps(t) {
    return [
        {
            selector: '.dashboard-header',
            content: ({ setCurrentStep }) => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step1.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step1.body')}
                    </p>
                    <button onClick={() => setCurrentStep(1)} style={tourButtonStyle}>
                        {t('dashboard.tour.step1.cta')}
                    </button>
                </div>
            ),
        },
        {
            selector: '[data-tour="lock-button"]',
            content: () => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step2.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step2.body_before')} <strong>{t('dashboard.tour.step2.body_bold')}</strong> {t('dashboard.tour.step2.body_after')}
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                        {t('dashboard.tour.must_click')}
                    </p>
                </div>
            ),
            action: (elem) => {
                elem.focus();
            }
        },
        {
            selector: '[data-tour="widgets-button"]',
            content: () => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step3.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step3.body')}
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                        {t('dashboard.tour.must_click')}
                    </p>
                </div>
            ),
        },
        {
            selector: '[data-tour="widget-sidebar"]',
            content: ({ setCurrentStep }) => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step4.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step4.body')}
                    </p>
                    <button onClick={() => setCurrentStep(4)} style={tourButtonStyle}>
                        {t('dashboard.tour.step4.cta')}
                    </button>
                </div>
            ),
        },
        {
            selector: '.widget-header',
            content: ({ setCurrentStep }) => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step5.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        • <strong>{t('dashboard.tour.step5.move_label')}</strong> {t('dashboard.tour.step5.move_text')}<br/>
                        • <strong>{t('dashboard.tour.step5.resize_label')}</strong> {t('dashboard.tour.step5.resize_text')}
                    </p>
                    <button onClick={() => setCurrentStep(5)} style={tourButtonStyle}>
                        {t('dashboard.tour.next')}
                    </button>
                </div>
            ),
        },
        {
            selector: '.widget-header',
            content: ({ setCurrentStep }) => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step6.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step6.body')}
                    </p>
                    <button onClick={() => setCurrentStep(6)} style={tourButtonStyle}>
                        {t('dashboard.tour.next')}
                    </button>
                </div>
            ),
        },
        {
            selector: '[data-tour="lock-button"]',
            content: () => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step7.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step7.body')}
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                        {t('dashboard.tour.must_click')}
                    </p>
                </div>
            ),
        },
        {
            selector: '[data-tour="lock-button"]',
            content: ({ setIsOpen }) => (
                <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#FF7233' }}>{t('dashboard.tour.step8.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', lineHeight: 1.6 }}>
                        {t('dashboard.tour.step8.body')}
                    </p>
                    <button onClick={() => setIsOpen(false)} style={tourButtonStyle}>
                        {t('dashboard.tour.step8.cta')}
                    </button>
                </div>
            ),
        },
    ];
}

export default function Dashboard() {
    const t = useTranslation('Dashboard.json');
    return (
        <TourProvider
            steps={buildTourSteps(t)}
            showNavigation={false}
            showBadge={true}
            showCloseButton={true}
            disableInteraction={false}
            disableDotsNavigation={true}
            styles={{
                popover: (base) => ({
                    ...base,
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                    padding: 0,
                    zIndex: 99999,
                }),
                badge: (base) => ({
                    ...base,
                    backgroundColor: '#FF7233',
                }),
                dot: (base) => ({
                    ...base,
                    backgroundColor: '#FF7233',
                }),
                maskWrapper: (base) => ({
                    ...base,
                    color: 'rgba(0, 0, 0, 0.7)',
                }),
                maskArea: (base) => ({
                    ...base,
                    rx: 8,
                }),
            }}
        >
            <DashboardContent />
        </TourProvider>
    );
}
